import { useEffect } from 'react';
import { useConfigStore } from '@/stores/useConfigStore';
import { useInputStore } from '@/stores/useInputStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { isByd } from '@/constants/bushingTypes';

/**
 * Compute the minimum Core OD from the config dialog parameters.
 *
 * Formula (ported from Python optibush):
 *   mod = sqrt((clamp_kN / allow_yield) * (4000 / π) + (core_id + 0.2)²)
 *         + 0.1 + (seal_mm * 2)
 *
 *   if min_area is provided:
 *     mod = max(mod, 2 * sqrt(min_area / π))
 *
 *   result = floor(mod * 10) / 10
 */
interface CoreOdMinResult {
  coreOdMin: number;
  /** Back-calculated min area — only populated when user hasn't provided one. */
  calculatedMinArea: number | null;
}

function computeCoreOdMin(
  clampKn: number | null,
  allowYield: number | null,
  coreIdVal: number | null,
  sealMm: number | null,
  minArea: number | null,
): CoreOdMinResult | null {
  if (clampKn == null || allowYield == null || coreIdVal == null || sealMm == null) {
    return null;
  }
  if (allowYield === 0) return null;

  let mod =
    Math.sqrt(
      (clampKn / allowYield) * (4000.0 / Math.PI) + (coreIdVal + 0.2) ** 2) + 0.1 + (sealMm * 2);

  if (minArea != null) {
    // User provided min area — use it directly, no need to calculate.
    mod = Math.max(mod, 2 * Math.sqrt(minArea / Math.PI));
    return {
      coreOdMin: Math.floor(mod * 10) / 10,
      calculatedMinArea: null,
    };
  }

  // User did NOT provide min area — back-calculate from mod.
  // Formula: (π/4) * ((mod - 0.1 - seal*2)² - (coreId + 0.2)²)
  const calculatedMinArea =
    (Math.PI / 4) * ((mod - 0.1 - sealMm * 2) ** 2 - (coreIdVal + 0.2) ** 2);

  return {
    coreOdMin: Math.floor(mod * 10) / 10,
    calculatedMinArea: Math.round(calculatedMinArea * 10) / 10,
  };
}

/**
 * useCoreOdMin — Reactive hook that subscribes to config store values
 * (clampLoad, allowableYield, sealRadial, minContactArea) and
 * packaging.coreId from the input store, then automatically updates
 * `packaging.coreOdMin` whenever any dependency changes.
 */
export function useCoreOdMin(): void {
  const clampLoad = useConfigStore((s) => s.clampLoad);
  const allowableYield = useConfigStore((s) => s.allowableYield);
  const sealRadial = useConfigStore((s) => s.sealRadial);
  const minContactArea = useConfigStore((s) => s.minContactArea);
  const coreId = useInputStore((s) => s.packaging.coreId);
  const bushingType = useBushingStore((s) => s.bushingType);

  useEffect(() => {
    // BYD mode: coreOdMin is set directly from the preset — do not overwrite it.
    if (isByd(bushingType)) return;

    const result = computeCoreOdMin(
      clampLoad,
      allowableYield,
      coreId,
      sealRadial,
      minContactArea,
    );

    const coreOdMin = result?.coreOdMin ?? null;
    const calculatedMinArea = result?.calculatedMinArea ?? null;

    // Always write the computed value (even null) so that stale values
    // are cleared when dependencies become null.
    const current = useInputStore.getState().packaging.coreOdMin;
    if (coreOdMin !== current) {
      useInputStore.getState().setPackagingField('coreOdMin', coreOdMin);
    }

    // Store calculated min area (never shown in UI) — used as fallback
    // when user has not explicitly provided a value.
    const currentCalcArea = useInputStore.getState().calculatedMinContactArea;
    if (calculatedMinArea !== currentCalcArea) {
      useInputStore.getState().setCalculatedMinContactArea(calculatedMinArea);
    }
  }, [clampLoad, allowableYield, coreId, sealRadial, minContactArea, bushingType]);
}
